import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export enum AuthErrorType {
  TOKEN_EXPIRED = 'token_expired',
  TOKEN_INVALID = 'token_invalid',
  REFRESH_FAILED = 'refresh_failed',
  NETWORK_ERROR = 'network_error',
  UNAUTHORIZED = 'unauthorized',
  FORBIDDEN = 'forbidden',
  UNKNOWN = 'unknown'
}

export interface AuthError {
  type: AuthErrorType;
  message: string;
  originalError?: any;
  shouldRetry: boolean;
  shouldLogout: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthErrorHandlerService {

  handleAuthError(error: any): AuthError {
    if (error instanceof HttpErrorResponse) {
      return this._handleHttpError(error);
    }

    if (error instanceof Error) {
      return this._handleGenericError(error);
    }

    return {
      type: AuthErrorType.UNKNOWN,
      message: 'An unknown error occurred',
      originalError: error,
      shouldRetry: false,
      shouldLogout: false
    };
  }

  private _handleHttpError(error: HttpErrorResponse): AuthError {
    switch (error.status) {
      case 401:
        return this._handle401Error(error);
      case 403:
        return {
          type: AuthErrorType.FORBIDDEN,
          message: 'Access denied',
          originalError: error,
          shouldRetry: false,
          shouldLogout: true
        };
      case 0:
      case 504:
      case 502:
      case 503:
        return {
          type: AuthErrorType.NETWORK_ERROR,
          message: 'Network error. Please check your connection.',
          originalError: error,
          shouldRetry: true,
          shouldLogout: false
        };
      default:
        return {
          type: AuthErrorType.UNKNOWN,
          message: `HTTP ${error.status}: ${error.message}`,
          originalError: error,
          shouldRetry: false,
          shouldLogout: false
        };
    }
  }

  private _handle401Error(error: HttpErrorResponse): AuthError {
    const errorMessage = error.error?.message || error.message;
    
    // Different types of 401 errors
    if (errorMessage?.includes('token') && errorMessage?.includes('expired')) {
      return {
        type: AuthErrorType.TOKEN_EXPIRED,
        message: 'Your session has expired',
        originalError: error,
        shouldRetry: true,
        shouldLogout: false
      };
    }

    if (errorMessage?.includes('refresh')) {
      return {
        type: AuthErrorType.REFRESH_FAILED,
        message: 'Unable to refresh your session',
        originalError: error,
        shouldRetry: false,
        shouldLogout: true
      };
    }

    return {
      type: AuthErrorType.UNAUTHORIZED,
      message: 'Authentication required',
      originalError: error,
      shouldRetry: false,
      shouldLogout: true
    };
  }

  private _handleGenericError(error: Error): AuthError {
    if (error.message?.includes('token')) {
      return {
        type: AuthErrorType.TOKEN_INVALID,
        message: 'Invalid authentication token',
        originalError: error,
        shouldRetry: false,
        shouldLogout: true
      };
    }

    return {
      type: AuthErrorType.UNKNOWN,
      message: error.message || 'An unexpected error occurred',
      originalError: error,
      shouldRetry: false,
      shouldLogout: false
    };
  }

  getRetryDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
    return Math.min(1000 * Math.pow(2, attempt), 30000);
  }

  shouldRetryAuth(error: AuthError, attemptCount: number): boolean {
    if (!error.shouldRetry || attemptCount >= 3) {
      return false;
    }

    return error.type === AuthErrorType.NETWORK_ERROR || 
           error.type === AuthErrorType.TOKEN_EXPIRED;
  }
}